import type { IconName } from '@/components/icon/Icon';
import type { DisplayRole } from '@/features/auth/roles';

/**
 * Side-nav items for the BankIQ AppShell, restructured to Ryan's v6
 * information architecture (SCRUM-322):
 *
 *   HOME     → Dashboard (the landing page)
 *   PLATFORM → Vault, Ask BankIQ, Referrals, Campaigns
 *
 * Platform ordering revised per client feedback (SCRUM-846): Vault leads and
 * Campaigns moves last. Data Ingestion stays last of all (upload surface).
 * NOTE: array position here IS the render order — SideNav renders each
 * NAV_GROUPS header then the matching NAV_ITEMS in array order. Nothing
 * indexes NAV_ITEMS positionally (landing paths resolve via ROLE_LANDING by
 * nav id), so reordering is presentation-only.
 *
 * The pre-v6 items Customer Dashboard, Intelligence, and Products are removed
 * (the new Dashboard replaces Intelligence as the home view; Customer
 * Dashboard becomes a Teller-only concept in the v6 role model, deferred with
 * SCRUM-103/290). Settings moved out of the nav groups into the SideNav
 * footer rail (next to Collapse and Sign Out), matching the v6 shell.
 *
 * Role-based visibility (SCRUM-387, v7): each item carries the exact
 * `data-roles` list extracted from Ryan's app-shell 7 prototype. The SideNav
 * filters by the active display role; groups with no visible items hide their
 * header. Role claims stay demo-era until SCRUM-102/103.
 */
export interface NavItem {
  /** Stable slug used by future TanStack Router (SCRUM-83+). */
  id: string;
  label: string;
  /**
   * Visual section the item appears under in the SideNav. `home` = the
   * landing dashboard; `platform` = day-to-day modules. Mirrors Ryan's v6
   * app-shell "Home" / "Platform" `.side-nav__section` grouping (the pre-v6
   * "Admin" section was removed — Settings lives in the footer rail).
   */
  group: 'home' | 'platform' | 'admin';
  /** v7 data-roles: display roles that see this item (exact prototype matrix). */
  roles: readonly DisplayRole[];
  /**
   * Brand icon name (SCRUM-278) — rendered via <Icon> when present. Set for the
   * nav items that have a Ryan brand glyph (sourced from app-shell.html).
   */
  iconName?: IconName;
  /** SCRUM key for the Story that will implement this page's real UI. */
  futureScrumKey: string;
}

const BANK_ACTORS: readonly DisplayRole[] = ['Bank Executive', 'Bank Officer', 'Branch Manager'];
const ALL_BANK_ROLES: readonly DisplayRole[] = [...BANK_ACTORS, 'Teller'];

/**
 * SCRUM-913. Bank Admin's server-side module grants are IDENTICAL to a Bank
 * Executive's (`ModuleAccessPolicy`: Executive / BankAdmin / PlatformAdmin =>
 * AllAccess), so every item a Bank Executive sees, a Bank Admin sees.
 *
 * Deliberately NOT folded into BANK_ACTORS: that constant carries the separate
 * "acts on referrals" meaning mirrored by `canActOnReferrals` (Exec / Officer /
 * Branch Manager), and widening it would silently change referral semantics.
 *
 * `nav-items.test.ts` asserts Bank Admin's visible set EQUALS Bank Executive's,
 * so a future item granted to one and not the other fails the test gate.
 */
const ADMIN_PEER: DisplayRole = 'Bank Admin';

export const NAV_ITEMS: readonly NavItem[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    group: 'home',
    roles: [...ALL_BANK_ROLES, ADMIN_PEER],
    iconName: 'dashboard',
    futureScrumKey: 'SCRUM-324',
  },
  {
    // Teller-only customer browser (v7 data-page teller-dashboard): the
    // campaigns ledger + drawer with campaign affordances stripped (SCRUM-389).
    id: 'teller-dashboard',
    label: 'Customer Dashboard',
    group: 'home',
    roles: ['Teller'],
    iconName: 'customer-dashboard',
    futureScrumKey: 'SCRUM-389',
  },
  {
    // Platform Admin's only surface (v7 data-page customer-dashboard):
    // BankIQ's customer banks — tenants (SCRUM-390/391).
    id: 'customer-dashboard',
    label: 'Customers',
    group: 'admin',
    roles: ['Platform Admin'],
    iconName: 'customer-dashboard',
    futureScrumKey: 'SCRUM-391',
  },
  {
    // Platform Admin: cross-tenant user activity — who's using the platform,
    // created date, last login, status (SCRUM-575).
    id: 'user-activity',
    label: 'User Activity',
    group: 'admin',
    roles: ['Platform Admin'],
    iconName: 'settings',
    futureScrumKey: 'SCRUM-575',
  },
  {
    id: 'vault',
    label: 'Vault',
    group: 'platform',
    // SCRUM-914: Bank Officer and Branch Manager REMOVED (was [...BANK_ACTORS, ADMIN_PEER]).
    // An officer may be an AVP, but that does not mean they should see the bank's uploaded
    // documents. This mirrors the server change — module:Vault was dropped from both roles in
    // ModuleAccessPolicy — so the nav is not merely hiding a link the API would still authorise.
    roles: ['Bank Executive', ADMIN_PEER],
    iconName: 'vault',
    futureScrumKey: 'SCRUM-135',
  },
  {
    id: 'ask-bankiq',
    label: 'Ask BankIQ',
    group: 'platform',
    roles: ['Bank Executive', ADMIN_PEER],
    iconName: 'ask-bankiq',
    futureScrumKey: 'SCRUM-83',
  },
  {
    id: 'referrals',
    label: 'Referrals',
    group: 'platform',
    roles: [...ALL_BANK_ROLES, ADMIN_PEER],
    iconName: 'referrals',
    futureScrumKey: 'SCRUM-329',
  },
  {
    id: 'campaigns',
    label: 'Campaigns',
    group: 'platform',
    roles: [...BANK_ACTORS, ADMIN_PEER],
    iconName: 'campaigns',
    futureScrumKey: 'SCRUM-30',
  },
  {
    // Data ingestion upload surface (SCRUM-677): deposit CSV extracts for the pipeline.
    // PLATFORM-ADMIN-ONLY — no bank role holds it. History: SCRUM-984 removed Bank
    // Executive (Rob's product call: depositing raw source extracts is a data-admin
    // function, not an executive business task) and left Bank Admin; Officer/Manager/
    // Teller were never granted it. Parity with the server
    // `ModuleAccessPolicy.IngestionRoles` set, which is its own gate independent of
    // the AllAccess module map.
    // SCRUM-914 narrowed this again to Platform Admin ONLY. SCRUM-984 (above) removed Bank
    // Executive and left Bank Admin; CJ then confirmed reversing the Bank Admin grant too —
    // ingesting raw core-banking extracts is an INTERSECT OPERATIONAL function, not a
    // bank-admin one. Parity with the server `ModuleAccessPolicy.IngestionRoles`.
    id: 'ingestion',
    label: 'Data Ingestion',
    group: 'platform',
    roles: ['Platform Admin'],
    iconName: 'vault',
    futureScrumKey: 'SCRUM-677',
  },
];

/**
 * The Settings entry rendered in the SideNav FOOTER rail (v6: next to
 * Collapse and Sign Out), not inside a nav group. Kept as a NavItem-shaped
 * object so AppShell can treat footer navigation identically to group
 * navigation (same activeId mechanism).
 */
export const FOOTER_SETTINGS_ITEM = {
  id: 'settings',
  label: 'Settings',
  iconName: 'settings' as IconName,
  futureScrumKey: 'SCRUM-118',
} as const;

/**
 * Ordered side-nav section descriptors. The SideNav renders one header per
 * group, in this order, followed by the NAV_ITEMS whose `group` matches.
 * Labels are stored Title-case (human-readable) and rendered uppercase via
 * CSS, matching Ryan's `.side-nav__section` ("HOME" / "PLATFORM").
 */
export const NAV_GROUPS = [
  { id: 'home', label: 'Home' },
  { id: 'admin', label: 'BankIQ Admin' },
  { id: 'platform', label: 'Platform' },
] as const satisfies readonly { id: NavItem['group']; label: string }[];

/**
 * Settings visibility. SCRUM-914: Bank Admin + Platform Admin ONLY — Bank
 * Executive is REMOVED. Administering users, roles, product priority and
 * integrations is an admin function rather than an executive business task
 * (CJ's ruling on Rob's request).
 *
 * Parity note: this mirrors the server's dedicated `ModuleAccessPolicy`
 * SettingsRoles set, so the footer entry is not merely hidden from an
 * Executive whose token would still be authorised by the API.
 *
 * The v7 prototype rendered this for Bank Executive only; that is now
 * historical — do not "restore" it from the prototype.
 */
export const SETTINGS_ROLES: readonly DisplayRole[] = [ADMIN_PEER, 'Platform Admin'];

/**
 * The id of the side-nav item that should be active on first render.
 *
 * SCRUM-322 (v6 IA): the landing view is the Dashboard home (replaces the
 * pre-v6 Intelligence Dashboard landing from SCRUM-274). Until real URL
 * routing lands (SCRUM-83), AppShell initialises its activeId useState from
 * this constant, so changing it changes the first-paint view.
 */
export const DEFAULT_NAV_ID: NavItem['id'] = 'dashboard';
